import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Sparkles, Search, Lock, Trophy, Clock, Zap, Info, TrendingUp, Target, Bookmark, Trash2, Play, Shield, Rocket, Flame, Coins } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Curated one-click query templates for beginners
const QUERY_TEMPLATES = [
  {
    id: 'safe-bets',
    title: 'Safe Bets',
    tag: 'Low risk',
    icon: Shield,
    accent: 'from-emerald-500/20 to-emerald-700/20 border-emerald-500/30',
    iconColor: 'text-emerald-400',
    description: 'High-probability picks around Over 1.5 goals where both teams have been scoring recently.',
    prompt: 'Show me upcoming matches in the next 3 days where the odds for over 1.5 goals are between 1.05 and 1.20, and both teams have scored in at least 80% of their last 5 games. Sort by highest probability.',
  },
  {
    id: 'weekend-value',
    title: 'Weekend Value',
    tag: 'Balanced',
    icon: Coins,
    accent: 'from-blue-500/20 to-blue-700/20 border-blue-500/30',
    iconColor: 'text-blue-400',
    description: 'Balanced Over 2.5 picks: fair odds meeting season averages and recent form.',
    prompt: 'Show me upcoming matches in the next 3 days where both teams average over 1.2 goals scored per match this season, both teams have scored in at least 70% of their last 5 games, H2H has over 2.5 goals in at least 60% of meetings, and odds for over 2.5 goals are between 1.60 and 2.20.',
  },
  {
    id: 'btts-specialists',
    title: 'BTTS Specialists',
    tag: 'Both teams to score',
    icon: Flame,
    accent: 'from-purple-500/20 to-purple-700/20 border-purple-500/30',
    iconColor: 'text-purple-400',
    description: 'Both-teams-to-score specialists — recent form + head-to-head confirm the pattern.',
    prompt: 'Show me upcoming matches in the next 3 days where BTTS happened in at least 80% of both teams\' last 5 games and H2H has BTTS in at least 70% of the last meetings, with BTTS Yes odds between 1.50 and 1.90.',
  },
  {
    id: 'high-odds',
    title: 'High Odds Chasers',
    tag: 'Higher risk / reward',
    icon: Rocket,
    accent: 'from-amber-500/20 to-orange-700/20 border-amber-500/30',
    iconColor: 'text-amber-400',
    description: 'Bigger returns from underdogs with strong scoring form vs leaky defences.',
    prompt: 'Show me upcoming matches in the next 3 days where one team has scored in 100% of their last 5 games and the opponent has conceded in at least 80% of their last 5 games, with odds for over 2.5 goals between 1.80 and 2.80.',
  },
];

// Human-readable target market labels
const MARKET_LABELS = {
  match_winner_home: 'Home Win',
  match_winner_away: 'Away Win',
  match_winner_draw: 'Draw',
  over_1_5: 'Over 1.5 Goals',
  over_2_5: 'Over 2.5 Goals',
  over_3_5: 'Over 3.5 Goals',
  under_1_5: 'Under 1.5 Goals',
  under_2_5: 'Under 2.5 Goals',
  under_3_5: 'Under 3.5 Goals',
  btts_yes: 'BTTS Yes',
  btts_no: 'BTTS No',
  double_chance_1x: '1X',
  double_chance_x2: 'X2',
  double_chance_12: '12',
};

const StatsQuery = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [quota, setQuota] = useState(null);
  const [savedQueries, setSavedQueries] = useState([]);
  const [saving, setSaving] = useState(false);

  const isPremium = user?.subscription_plan && user.subscription_plan !== 'free';
  const isAdmin = user?.is_admin;
  // Trial is active if the quota endpoint says so — this only matters for free users
  const trialActive = quota?.tier === 'trial';
  const trialExpired = quota?.tier === 'expired';
  const canAccess = isPremium || isAdmin || trialActive;

  useEffect(() => {
    if (user) fetchQuota();
  }, [user]);

  useEffect(() => {
    if (canAccess) fetchSavedQueries();
  }, [canAccess]);

  const fetchQuota = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const { data } = await axios.get(`${API}/stats/quota`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setQuota(data);
    } catch (e) { /* ignore */ }
  };

  const fetchSavedQueries = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const { data } = await axios.get(`${API}/stats/saved-queries`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSavedQueries(data.queries || []);
    } catch (e) { /* ignore */ }
  };

  const saveCurrentPrompt = async () => {
    if (prompt.trim().length < 10) {
      toast.error('Enter a prompt first (at least 10 characters).');
      return;
    }
    const label = window.prompt('Give this query a short label (or leave blank for auto-label):', '');
    if (label === null) return; // user cancelled
    setSaving(true);
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(
        `${API}/stats/saved-queries`,
        { prompt: prompt.trim(), label: label || '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Query saved.');
      fetchSavedQueries();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save query.');
    } finally {
      setSaving(false);
    }
  };

  const deleteSavedQuery = async (queryId) => {
    if (!window.confirm('Delete this saved query?')) return;
    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/stats/saved-queries/${queryId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Saved query deleted.');
      fetchSavedQueries();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed.');
    }
  };

  const runSavedQuery = async (q) => {
    setPrompt(q.prompt);
    // Give the state one tick to flush, then run
    setTimeout(() => runQueryWithPrompt(q.prompt), 50);
  };

  const runQuery = async () => {
    if (prompt.trim().length < 10) {
      toast.error('Please describe what matches you\'re looking for (at least 10 characters).');
      return;
    }
    await runQueryWithPrompt(prompt.trim());
  };

  const runQueryWithPrompt = async (p) => {
    setLoading(true);
    setResult(null);
    try {
      const token = localStorage.getItem('session_token');
      const { data } = await axios.post(
        `${API}/stats/query`,
        { prompt: p },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 180000 }
      );
      setResult(data);
      setQuota(data.quota);
      if (data.matches?.length === 0) {
        toast.info('No matches found for your criteria. Try relaxing some thresholds.');
      } else {
        toast.success(`Found ${data.matches.length} matching ${data.matches.length === 1 ? 'game' : 'games'}${data.cached ? ' (cached)' : ''}.`);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'Query failed. Please try again.';
      toast.error(msg);
      if (e.response?.status === 429) fetchQuota();
    } finally {
      setLoading(false);
    }
  };

  const probabilityColor = (p) => {
    if (p == null) return 'text-gray-400';
    if (p >= 80) return 'text-emerald-400';
    if (p >= 60) return 'text-blue-400';
    if (p >= 40) return 'text-amber-400';
    return 'text-rose-400';
  };

  const probabilityBg = (p) => {
    if (p == null) return 'from-slate-500/20 to-slate-700/20 border-slate-500/30';
    if (p >= 80) return 'from-emerald-500/20 to-emerald-700/20 border-emerald-500/30';
    if (p >= 60) return 'from-blue-500/20 to-blue-700/20 border-blue-500/30';
    if (p >= 40) return 'from-amber-500/20 to-amber-700/20 border-amber-500/30';
    return 'from-rose-500/20 to-rose-700/20 border-rose-500/30';
  };

  if (!canAccess) {
    // Free user with expired trial OR no quota fetched yet
    const showExpiredCopy = trialExpired;
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4 sm:p-6 lg:p-8">
          <div className="max-w-3xl mx-auto py-16">
            <Card className="bg-gradient-to-br from-amber-500/20 to-orange-700/20 border-amber-500/30">
              <CardContent className="text-center py-16">
                <Lock className="w-16 h-16 text-amber-400 mx-auto mb-6" />
                <h2 className="text-3xl font-bold text-white mb-3">
                  {showExpiredCopy ? 'Free Trial Expired' : 'Stats Query'}
                </h2>
                <p className="text-gray-300 max-w-lg mx-auto mb-8">
                  {showExpiredCopy
                    ? "Your 7-day free trial of Stats Query has ended. Upgrade to Premium to keep asking natural-language questions and get ranked upcoming matches."
                    : "Describe the type of upcoming matches you're hunting for in plain English and get a ranked list back. Free 7-day trial for all signed-in users, then it's part of Premium."}
                </p>
                <Button
                  onClick={() => navigate('/subscription')}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-full"
                  data-testid="stats-upgrade-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" /> Upgrade to Premium
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Zap className="w-8 h-8 text-blue-400" />
                <h1 className="text-3xl sm:text-4xl font-bold text-white">Stats Query</h1>
                {trialActive ? (
                  <span className="text-xs bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 px-2 py-1 rounded-full font-semibold" data-testid="stats-trial-badge">
                    Free Trial · {Math.max(1, Math.ceil(quota?.trial_days_remaining ?? 7))}d left
                  </span>
                ) : (
                  <span className="text-xs bg-amber-500/20 text-amber-300 border border-amber-500/30 px-2 py-1 rounded-full font-semibold">Premium</span>
                )}
              </div>
              <p className="text-gray-300 text-sm sm:text-base max-w-2xl">
                Describe the type of upcoming matches you're looking for. Our engine parses your
                criteria and searches team season stats, recent form, H2H and live odds — then
                returns a list ranked by probability.
              </p>
            </div>
            {quota && (
              <Card className="bg-gradient-to-br from-blue-500/20 to-blue-700/20 border-blue-500/30 shrink-0">
                <CardContent className="pt-4 pb-4 px-5 text-right">
                  <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">Daily Queries</p>
                  {quota.unlimited ? (
                    <>
                      <p className="text-2xl font-bold text-white" data-testid="stats-quota-display">∞</p>
                      <p className="text-xs text-amber-300 mt-1">Admin · Unlimited</p>
                    </>
                  ) : (
                    <>
                      <p className="text-2xl font-bold text-white" data-testid="stats-quota-display">
                        {quota.used}<span className="text-gray-500">/{quota.limit}</span>
                      </p>
                      <p className="text-xs text-gray-400 mt-1">{quota.remaining} remaining today</p>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Trial banner */}
          {trialActive && (
            <Card className="bg-gradient-to-br from-emerald-500/15 to-cyan-500/15 border-emerald-500/30" data-testid="stats-trial-banner">
              <CardContent className="py-3 px-5 flex items-center justify-between flex-wrap gap-3">
                <p className="text-sm text-emerald-200">
                  <span className="font-semibold">Free trial active</span> — {Math.max(1, Math.ceil(quota?.trial_days_remaining ?? 7))} day(s) left.
                  You have full access to Stats Query. Upgrade any time to keep it after the trial ends.
                </p>
                <Button
                  size="sm"
                  onClick={() => navigate('/subscription')}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
                  data-testid="stats-trial-upgrade-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" /> Upgrade to Premium
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Prompt Input */}
          <Card className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Search className="w-5 h-5 text-blue-400" /> Your Query
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="e.g. Show me all upcoming matches in the next 3 days where the odds are over 1.05 for 1.5 goals."
                rows={5}
                maxLength={1500}
                disabled={loading}
                className="bg-slate-950/60 border-white/10 text-white placeholder:text-gray-500 focus-visible:ring-blue-500"
                data-testid="stats-prompt-input"
              />
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="text-xs text-gray-400">{prompt.length}/1500 characters</div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={saveCurrentPrompt}
                    disabled={loading || saving || prompt.trim().length < 10}
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10 rounded-full"
                    data-testid="stats-save-btn"
                  >
                    <Bookmark className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save'}
                  </Button>
                  <Button
                    onClick={runQuery}
                    disabled={loading || (quota && !quota.unlimited && quota.remaining <= 0)}
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-full btn-hover"
                    data-testid="stats-submit-btn"
                  >
                    {loading ? (
                      <><Clock className="w-4 h-4 mr-2 animate-spin" /> Analyzing…</>
                    ) : (
                      <><Sparkles className="w-4 h-4 mr-2" /> Search Matches</>
                    )}
                  </Button>
                </div>
              </div>

              {/* Saved queries */}
              {savedQueries.length > 0 && (
                <div className="pt-4 border-t border-white/10">
                  <div className="text-sm text-gray-300 mb-3 flex items-center gap-2">
                    <Bookmark className="w-4 h-4 text-amber-400" /> Your saved queries
                  </div>
                  <div className="grid gap-2 min-w-0" data-testid="stats-saved-queries">
                    {savedQueries.map((q) => (
                      <div
                        key={q.id}
                        className="flex items-start gap-2 w-full min-w-0 bg-white/5 border border-white/10 rounded-lg px-3 py-2 hover:border-amber-500/40 transition"
                      >
                        <div className="flex-1 min-w-0 overflow-hidden">
                          <p className="text-sm font-semibold text-white truncate">{q.label}</p>
                          <p className="text-xs text-gray-400 truncate">{q.prompt}</p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => runSavedQuery(q)}
                          disabled={loading || (quota && !quota.unlimited && quota.remaining <= 0)}
                          className="bg-blue-600 hover:bg-blue-700 text-white h-8 shrink-0"
                          data-testid={`stats-run-saved-${q.id}`}
                        >
                          <Play className="w-3 h-3 mr-1" /> Run
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => deleteSavedQuery(q.id)}
                          variant="outline"
                          className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10 h-8 shrink-0"
                          data-testid={`stats-delete-saved-${q.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Query Templates - one-click presets for beginners */}
              <div className="pt-4 border-t border-white/10">
                <div className="text-sm text-gray-300 mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-blue-400" /> Query templates — one click to try:
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {QUERY_TEMPLATES.map((t) => {
                    const Icon = t.icon;
                    return (
                      <button
                        key={t.id}
                        onClick={() => setPrompt(t.prompt)}
                        disabled={loading}
                        className={`text-left bg-gradient-to-br ${t.accent} border rounded-xl p-4 hover:brightness-110 transition disabled:opacity-60`}
                        data-testid={`stats-template-${t.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-lg bg-white/10 shrink-0">
                            <Icon className={`w-5 h-5 ${t.iconColor}`} />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="font-bold text-white">{t.title}</span>
                              <span className="text-[10px] uppercase tracking-wide text-gray-400 bg-white/5 border border-white/10 px-1.5 py-0.5 rounded">{t.tag}</span>
                            </div>
                            <p className="text-xs text-gray-300 leading-relaxed">{t.description}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 mt-3 flex items-center gap-2">
                  <Info className="w-3 h-3" /> Templates fill the prompt — tweak it if you want, then hit "Search Matches".
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Loading */}
          {loading && (
            <Card className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 border-white/10">
              <CardContent className="py-10 text-center">
                <Clock className="w-10 h-10 text-blue-400 mx-auto mb-4 animate-spin" />
                <p className="text-white">Parsing your prompt and scanning upcoming fixtures…</p>
                <p className="text-gray-400 text-sm mt-2">This can take 20–60 seconds.</p>
              </CardContent>
            </Card>
          )}

          {/* Interpreted criteria */}
          {result && !loading && result.filters && (
            <Card className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 border-white/10">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-400" /> Interpreted Criteria
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-300 text-sm mb-3">{result.filters.notes || '—'}</p>
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="bg-white/5 border border-white/10 text-gray-300 px-2 py-1 rounded">Next {result.filters.days_ahead}d</span>
                  {result.filters.target_market && (
                    <span className="bg-blue-500/20 border border-blue-500/30 text-blue-300 px-2 py-1 rounded font-semibold">
                      Market: {MARKET_LABELS[result.filters.target_market] || result.filters.target_market}
                    </span>
                  )}
                  <span className="bg-white/5 border border-white/10 text-gray-300 px-2 py-1 rounded">Scanned: {result.total_scanned}</span>
                  <span className="bg-white/5 border border-white/10 text-gray-300 px-2 py-1 rounded">Checked: {result.candidates_checked ?? 0}</span>
                  <span className="bg-white/5 border border-white/10 text-gray-300 px-2 py-1 rounded">Returned: {result.matches?.length ?? 0}</span>
                  {result.elapsed_seconds != null && (
                    <span data-testid="stats-took-chip" className="bg-white/5 border border-white/10 text-gray-300 px-2 py-1 rounded">Took: {result.elapsed_seconds}s</span>
                  )}
                  {result.partial && (
                    <span data-testid="stats-partial-chip" className="bg-amber-500/20 border border-amber-500/30 text-amber-300 px-2 py-1 rounded" title="Query hit the time budget; some fixture details were skipped. Results may be incomplete.">⚠ Partial (time budget)</span>
                  )}
                  {result.cached && <span className="bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 px-2 py-1 rounded">Cached (2h)</span>}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Results */}
          {result && !loading && (
            result.matches?.length === 0 ? (
              <Card className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 border-white/10">
                <CardContent className="py-16 text-center">
                  <Trophy className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                  <p className="text-white font-semibold mb-2">No matches found</p>
                  <p className="text-gray-400 text-sm">Try relaxing your thresholds or extending the date range.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3" data-testid="stats-results-list">
                {result.matches.map((m, idx) => (
                  <Card
                    key={m.fixture_id}
                    className={`bg-gradient-to-br ${probabilityBg(m.target_probability)} hover:brightness-110 transition`}
                  >
                    <CardContent className="p-4 sm:p-5">
                      <div className="flex items-start justify-between flex-wrap gap-4">
                        {/* Left: rank + league + teams + kickoff */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span className="bg-white/10 text-white text-xs font-bold rounded-full px-2 py-0.5">#{idx + 1}</span>
                            <span className="text-sm font-semibold text-white">{m.country}</span>
                            <span className="text-gray-400">·</span>
                            <span className="text-sm text-blue-300 font-medium">{m.league_name}</span>
                          </div>
                          <div className="flex items-center gap-3 flex-wrap">
                            {m.home_logo && <img src={m.home_logo} alt="" className="w-7 h-7 rounded" />}
                            <span className="text-white font-bold text-lg">{m.home_team}</span>
                            <span className="text-gray-500">vs</span>
                            {m.away_logo && <img src={m.away_logo} alt="" className="w-7 h-7 rounded" />}
                            <span className="text-white font-bold text-lg">{m.away_team}</span>
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            {new Date(m.start_time).toLocaleString()}
                          </div>
                        </div>

                        {/* Right: probability + odds */}
                        <div className="text-right">
                          {m.target_probability != null && (
                            <>
                              <p className="text-xs uppercase tracking-wide text-gray-400 flex items-center justify-end gap-1">
                                <Target className="w-3 h-3" /> Probability
                              </p>
                              <p className={`text-3xl font-bold ${probabilityColor(m.target_probability)}`}>
                                {m.target_probability.toFixed(1)}%
                              </p>
                            </>
                          )}
                          {m.target_odds && (
                            <p className="text-xs text-gray-300 mt-1">
                              {MARKET_LABELS[m.target_market] || m.target_market} @ <span className="text-blue-300 font-semibold">{m.target_odds.toFixed(2)}</span>
                            </p>
                          )}
                          {m.target_probability == null && (
                            <>
                              <p className="text-xs uppercase tracking-wide text-gray-400">Score</p>
                              <p className="text-2xl font-bold text-blue-400">{m.score.toFixed(1)}</p>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Matched criteria chips */}
                      {m.matched_criteria?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {m.matched_criteria.map((c, i) => (
                            <span key={i} className="text-xs bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 px-2 py-1 rounded-full">
                              ✓ {c}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Detail stats */}
                      {(m.home_stats || m.away_stats || m.home_form || m.away_form || m.h2h) && (
                        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                          {m.home_stats && (
                            <div className="bg-white/5 border border-white/10 rounded p-2">
                              <div className="text-gray-400">Home season</div>
                              <div className="text-white">GF/g {m.home_stats.avg_scored} · GA/g {m.home_stats.avg_conceded}</div>
                            </div>
                          )}
                          {m.away_stats && (
                            <div className="bg-white/5 border border-white/10 rounded p-2">
                              <div className="text-gray-400">Away season</div>
                              <div className="text-white">GF/g {m.away_stats.avg_scored} · GA/g {m.away_stats.avg_conceded}</div>
                            </div>
                          )}
                          {m.home_form && (
                            <div className="bg-white/5 border border-white/10 rounded p-2">
                              <div className="text-gray-400">Home last {m.home_form.sample}</div>
                              <div className="text-white">Scored {m.home_form.scored_pct}% · BTTS {m.home_form.btts_pct}%</div>
                              {m.home_form.no_loss_pct != null && (
                                <div className="text-gray-300">W {m.home_form.won_pct}% · D {m.home_form.drew_pct}% · L {m.home_form.lost_pct}%</div>
                              )}
                            </div>
                          )}
                          {m.away_form && (
                            <div className="bg-white/5 border border-white/10 rounded p-2">
                              <div className="text-gray-400">Away last {m.away_form.sample}</div>
                              <div className="text-white">Scored {m.away_form.scored_pct}% · BTTS {m.away_form.btts_pct}%</div>
                              {m.away_form.no_loss_pct != null && (
                                <div className="text-gray-300">W {m.away_form.won_pct}% · D {m.away_form.drew_pct}% · L {m.away_form.lost_pct}%</div>
                              )}
                            </div>
                          )}
                          {m.h2h && (
                            <div className="bg-white/5 border border-white/10 rounded p-2 col-span-2 md:col-span-4">
                              <div className="text-gray-400">H2H last {m.h2h.sample}</div>
                              <div className="text-white">Over 1.5 {m.h2h.over_1_5_pct}% · Over 2.5 {m.h2h.over_2_5_pct}% · BTTS {m.h2h.btts_pct}%</div>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </Layout>
  );
};

export default StatsQuery;
